import React from 'react';
import { Pressable } from 'react-native';

import { Box, useTheme } from 'app/shopify/theme';

import { HeartFilledIcon } from './HeartFilledIcon';
import { HeartIcon } from './HeartIcon';

type ToggleIconButtonType = 'primary' | 'ghost';
type ToggleIconButtonSize = 'small' | 'medium';
type ToggleIconButtonState = 'enabled' | 'hover' | 'pressed';

export interface ToggleIconButtonProps {
  selected?: boolean;
  type?: ToggleIconButtonType;
  size?: ToggleIconButtonSize;
  state?: ToggleIconButtonState;
  inverted?: boolean;
  icon?: React.ReactNode;
  selectedIcon?: React.ReactNode;
  onPress?: () => void;
}

const BUTTON_SIZE_BY_SIZE: Record<ToggleIconButtonSize, number> = {
  small: 32,
  medium: 40,
};

const ICON_SIZE_BY_SIZE: Record<ToggleIconButtonSize, number> = {
  small: 20,
  medium: 24,
};

interface PressableInteractionState {
  pressed: boolean;
  hovered?: boolean;
}

export function ToggleIconButton({
  selected = false,
  type = 'ghost',
  size = 'medium',
  state,
  inverted = false,
  icon,
  selectedIcon,
  onPress,
}: ToggleIconButtonProps) {
  const theme = useTheme();
  const isInteractive = state === undefined || state === 'enabled';

  const backgroundColor =
    type === 'primary' ? theme.colors[inverted ? 'surface.default' : 'surface.brand.strong'] : 'transparent';

  const iconColor = selected
    ? type === 'primary' && !inverted
      ? theme.colors['text.inverse.primary']
      : theme.colors['text.brand']
    : inverted
      ? theme.colors['text.inverse.primary']
      : theme.colors['text.brand'];

  function getOverlayColor(showPressed: boolean, showHover: boolean) {
    if (!showPressed && !showHover) return undefined;
    return theme.colors[showPressed ? 'state-layer.press-brand' : 'state-layer.hover-brand'];
  }

  function renderInner(interaction?: PressableInteractionState) {
    const pressed = interaction?.pressed ?? false;
    const hovered = interaction?.hovered ?? false;
    const showPressed = isInteractive ? pressed : state === 'pressed';
    const showHover = isInteractive ? hovered && !pressed : state === 'hover';
    const overlayColor = getOverlayColor(showPressed, showHover);
    const iconSize = ICON_SIZE_BY_SIZE[size];

    return (
      <Box
        alignItems="center"
        justifyContent="center"
        borderRadius="radius.full"
        style={{
          width: BUTTON_SIZE_BY_SIZE[size],
          height: BUTTON_SIZE_BY_SIZE[size],
          backgroundColor,
        }}
      >
        {overlayColor ? (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            borderRadius="radius.full"
            style={{ backgroundColor: overlayColor, zIndex: 0 }}
          />
        ) : null}

        <Box alignItems="center" justifyContent="center" style={{ zIndex: 1 }}>
          {selected
            ? (selectedIcon ?? <HeartFilledIcon size={iconSize} color={iconColor} />)
            : (icon ?? <HeartIcon size={iconSize} color={iconColor} />)}
        </Box>
      </Box>
    );
  }

  if (isInteractive) {
    return (
      <Pressable onPress={onPress} accessibilityRole="button" accessibilityState={{ selected }}>
        {(pressableState) =>
          renderInner({
            pressed: pressableState.pressed,
            hovered: 'hovered' in pressableState ? (pressableState as PressableInteractionState).hovered : false,
          })
        }
      </Pressable>
    );
  }

  return (
    <Pressable onPress={onPress} accessibilityRole="button" accessibilityState={{ selected }}>
      {renderInner()}
    </Pressable>
  );
}
